import {
  groupKitLibraryImportControllerImportMeetings,
  groupMeetingControllerGetMeetingPreview,
  groupMeetingControllerListMeetings,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export function useConnectorMeetingsData(
  groupUuid: string | null,
  connectorUuid: string | null,
  kitUuid: string | null,
  search: string,
) {
  return useInfiniteQuery({
    queryKey: ["connector-meetings", groupUuid, connectorUuid, kitUuid, search],
    enabled: !!groupUuid && !!connectorUuid,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await groupMeetingControllerListMeetings({
        path: {
          groupUuid: groupUuid!,
        },
        query: {
          connector_uuid: connectorUuid!,
          cursor: pageParam,
          search: search.trim() || undefined,
          kit_uuid: kitUuid ?? undefined,
        },
      });

      if (response.error) {
        throw response.error;
      }

      return {
        data: response.data?.data ?? [],
        nextCursor: response.data?.next_cursor ?? null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useMeetingPreviewData(
  groupUuid: string | null,
  connectorUuid: string | null,
  meetingId: string | null,
) {
  return useQuery({
    queryKey: ["meeting-preview", groupUuid, connectorUuid, meetingId],
    enabled: !!groupUuid && !!connectorUuid && !!meetingId,
    queryFn: async () => {
      const response = await groupMeetingControllerGetMeetingPreview({
        path: {
          groupUuid: groupUuid!,
          meetingId: meetingId!,
        },
        query: {
          connector_uuid: connectorUuid!,
        },
      });
      return response.data ?? null;
    },
  });
}

export function useImportMeetingsMutation(
  groupUuid: string | null,
  kitUuid: string | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectorUuid,
      meetingIds,
    }: {
      connectorUuid: string;
      meetingIds: string[];
    }) => {
      const response = await groupKitLibraryImportControllerImportMeetings({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
        body: {
          connectorUuid,
          meetingIds,
        },
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.files(),
      });
      queryClient.invalidateQueries({
        queryKey: ["connector-meetings", groupUuid, variables.connectorUuid],
      });
    },
  });
}

export function formatMeetingDuration(
  seconds: number | null | undefined,
): string {
  if (seconds == null || seconds <= 0) {
    return "—";
  }

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}
